import React, { createContext, useContext, useReducer, useRef, useCallback, useMemo, ReactNode } from 'react';
import { Room, LocalParticipant, RemoteParticipant } from 'livekit-client';
import {
  LiveStudioContextType,
  RoomState,
  LiveStreamState,
  UIControlsState,
  ConnectionState,
  ConnectionQualityInfo,
  VideoElementRefs,
  Participant,
  ChatMessage,
  DeviceSelectionState
} from '../types';
import { useVideoRefs } from '../hooks/useVideoRefs';
import { useDeviceSelection } from '../hooks/useDeviceSelection';

// Action types
type LiveStudioAction =
  | { type: 'SET_ROOM'; payload: Room | null }
  | { type: 'SET_LOCAL_PARTICIPANT'; payload: LocalParticipant | null }
  | { type: 'SET_PARTICIPANTS'; payload: Participant[] }
  | { type: 'SET_IS_CONNECTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SHOW_PRE_JOIN'; payload: boolean }
  | { type: 'SET_ROOM_NAME'; payload: string }
  | { type: 'SET_SHOW_LEAVE_CONFIRMATION'; payload: boolean }
  | { type: 'SET_LIVEKIT_TOKEN'; payload: string | null }
  | { type: 'SET_LIVE_STREAMING'; payload: boolean }
  | { type: 'SET_STARTING_STREAM'; payload: boolean }
  | { type: 'SET_STOPPING_STREAM'; payload: boolean }
  | { type: 'SET_ACTIVE_EGRESS_ID'; payload: string | null }
  | { type: 'SET_STREAM_ERROR'; payload: string | null }
  | { type: 'SET_SHOW_CHAT'; payload: boolean }
  | { type: 'SET_SHOW_PARTICIPANTS'; payload: boolean }
  | { type: 'SET_SHOW_SETTINGS'; payload: boolean }
  | { type: 'SET_SHOW_COMMENTS'; payload: boolean }
  | { type: 'SET_SHOW_CHANNEL_CONTROL'; payload: boolean }
  | { type: 'SET_CHAT_MESSAGES'; payload: ChatMessage[] }
  | { type: 'SET_NEW_MESSAGE'; payload: string }
  | { type: 'SET_HAS_CONNECTED'; payload: boolean }
  | { type: 'SET_CONNECTION_ATTEMPTS'; payload: number }
  | { type: 'SET_DEVICE_SELECTION_STATE'; payload: DeviceSelectionState }
  | { type: 'SET_CONNECTION_QUALITY_INFO'; payload: ConnectionQualityInfo }
  | { type: 'RESET_STATE' };

// Initial state
const initialState = {
  roomState: {
    room: null,
    participants: [],
    localParticipant: null,
    isConnecting: false,
    error: null,
    showPreJoin: true,
    roomName: '',
    showLeaveConfirmation: false,
    livekitToken: null,
  } as RoomState,
  liveStreamState: {
    isLiveStreaming: false,
    isStartingStream: false,
    isStoppingStream: false,
    activeEgressId: null,
    streamError: null,
  } as LiveStreamState,
  uiControlsState: {
    showChat: false,
    showParticipants: false,
    showSettings: false,
    showComments: false,
    showChannelControl: false,
    chatMessages: [],
    newMessage: '',
  } as UIControlsState,
  connectionState: {
    hasConnected: false,
    isConnecting: false,
    connectionAttempts: 0,
  } as ConnectionState,
  connectionQualityInfo: {
    level: 'unknown',
    quality: 'MEDIUM',
    deviceCapabilities: undefined,
  } as ConnectionQualityInfo,
  deviceSelectionState: {
    cameras: [],
    microphones: [],
    selectedCameraId: null,
    selectedMicrophoneId: null,
    isLoading: false,
    error: null,
  } as DeviceSelectionState,
};

// Reducer
const liveStudioReducer = (state: typeof initialState, action: LiveStudioAction): typeof initialState => {
  switch (action.type) {
    case 'SET_ROOM':
      return {
        ...state,
        roomState: { ...state.roomState, room: action.payload }
      };
    case 'SET_LOCAL_PARTICIPANT':
      return {
        ...state,
        roomState: { ...state.roomState, localParticipant: action.payload }
      };
    case 'SET_PARTICIPANTS':
      return {
        ...state,
        roomState: { ...state.roomState, participants: action.payload }
      };
    case 'SET_IS_CONNECTING':
      return {
        ...state,
        roomState: { ...state.roomState, isConnecting: action.payload },
        connectionState: { ...state.connectionState, isConnecting: action.payload }
      };
    case 'SET_ERROR':
      return {
        ...state,
        roomState: { ...state.roomState, error: action.payload }
      };
    case 'SET_SHOW_PRE_JOIN':
      return {
        ...state,
        roomState: { ...state.roomState, showPreJoin: action.payload }
      };
    case 'SET_ROOM_NAME':
      return {
        ...state,
        roomState: { ...state.roomState, roomName: action.payload }
      };
    case 'SET_SHOW_LEAVE_CONFIRMATION':
      return {
        ...state,
        roomState: { ...state.roomState, showLeaveConfirmation: action.payload }
      };
    case 'SET_LIVEKIT_TOKEN':
      return {
        ...state,
        roomState: { ...state.roomState, livekitToken: action.payload }
      };
    case 'SET_LIVE_STREAMING':
      return {
        ...state,
        liveStreamState: { ...state.liveStreamState, isLiveStreaming: action.payload }
      };
    case 'SET_STARTING_STREAM':
      return {
        ...state,
        liveStreamState: { ...state.liveStreamState, isStartingStream: action.payload }
      };
    case 'SET_STOPPING_STREAM':
      return {
        ...state,
        liveStreamState: { ...state.liveStreamState, isStoppingStream: action.payload }
      };
    case 'SET_ACTIVE_EGRESS_ID':
      return {
        ...state,
        liveStreamState: { ...state.liveStreamState, activeEgressId: action.payload }
      };
    case 'SET_STREAM_ERROR':
      return {
        ...state,
        liveStreamState: { ...state.liveStreamState, streamError: action.payload }
      };
    case 'SET_SHOW_CHAT':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, showChat: action.payload }
      };
    case 'SET_SHOW_PARTICIPANTS':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, showParticipants: action.payload }
      };
    case 'SET_SHOW_SETTINGS':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, showSettings: action.payload }
      };
    case 'SET_SHOW_COMMENTS':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, showComments: action.payload }
      };
    case 'SET_SHOW_CHANNEL_CONTROL':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, showChannelControl: action.payload }
      };
    case 'SET_CHAT_MESSAGES':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, chatMessages: action.payload }
      };
    case 'SET_NEW_MESSAGE':
      return {
        ...state,
        uiControlsState: { ...state.uiControlsState, newMessage: action.payload }
      };
    case 'SET_HAS_CONNECTED':
      return {
        ...state,
        connectionState: { ...state.connectionState, hasConnected: action.payload }
      };
    case 'SET_CONNECTION_ATTEMPTS':
      return {
        ...state,
        connectionState: { ...state.connectionState, connectionAttempts: action.payload }
      };
    case 'SET_DEVICE_SELECTION_STATE':
      return {
        ...state,
        deviceSelectionState: action.payload
      };
    case 'SET_CONNECTION_QUALITY_INFO':
      return {
        ...state,
        connectionQualityInfo: action.payload
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

// Create context
const LiveStudioContext = createContext<LiveStudioContextType | undefined>(undefined);

// Provider component
interface LiveStudioProviderProps {
  children: ReactNode;
  profile: { token: string };
  events: any;
  roomState?: {
    room: Room | null;
    localParticipant: LocalParticipant | null;
  };
  videoRefs?: VideoElementRefs;
}

export const LiveStudioProvider: React.FC<LiveStudioProviderProps> = ({
  children,
  profile,
  events,
  roomState: externalRoomState,
  videoRefs: externalVideoRefs
}) => {
  const [state, dispatch] = useReducer(liveStudioReducer, initialState);

  // Video refs - use external refs if provided, otherwise create new ones
  const internalVideoRefs = useVideoRefs();
  const videoRefs = externalVideoRefs || internalVideoRefs;

  // Device selection hook - use external room state if available, otherwise use context state
  const deviceSelection = useDeviceSelection({
    localParticipant: externalRoomState?.localParticipant || state.roomState.localParticipant,
    room: externalRoomState?.room || state.roomState.room,
    videoRefs: videoRefs,
    isLiveStreaming: state.liveStreamState.isLiveStreaming,
  });

  // Debug logging
  console.log('LiveStudioContext: externalRoomState:', {
    hasRoom: !!externalRoomState?.room,
    hasLocalParticipant: !!externalRoomState?.localParticipant,
    roomState: !!state.roomState.room,
    localParticipantState: !!state.roomState.localParticipant,
  });

  // Action creators
  const setRoom = useCallback((room: Room | null) => {
    dispatch({ type: 'SET_ROOM', payload: room });
  }, []);

  const setLocalParticipant = useCallback((participant: LocalParticipant | null) => {
    dispatch({ type: 'SET_LOCAL_PARTICIPANT', payload: participant });
  }, []);

  const setParticipants = useCallback((participants: Participant[]) => {
    dispatch({ type: 'SET_PARTICIPANTS', payload: participants });
  }, []);

  const setIsConnecting = useCallback((isConnecting: boolean) => {
    dispatch({ type: 'SET_IS_CONNECTING', payload: isConnecting });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setShowPreJoin = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_PRE_JOIN', payload: show });
  }, []);

  const setRoomName = useCallback((name: string) => {
    dispatch({ type: 'SET_ROOM_NAME', payload: name });
  }, []);

  const setShowLeaveConfirmation = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_LEAVE_CONFIRMATION', payload: show });
  }, []);

  const setLivekitToken = useCallback((token: string | null) => {
    dispatch({ type: 'SET_LIVEKIT_TOKEN', payload: token });
  }, []);

  const setIsLiveStreaming = useCallback((isLiveStreaming: boolean) => {
    dispatch({ type: 'SET_LIVE_STREAMING', payload: isLiveStreaming });
  }, []);

  const setIsStartingStream = useCallback((isStarting: boolean) => {
    dispatch({ type: 'SET_STARTING_STREAM', payload: isStarting });
  }, []);

  const setIsStoppingStream = useCallback((isStopping: boolean) => {
    dispatch({ type: 'SET_STOPPING_STREAM', payload: isStopping });
  }, []);

  const setActiveEgressId = useCallback((egressId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_EGRESS_ID', payload: egressId });
  }, []);

  const setStreamError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_STREAM_ERROR', payload: error });
  }, []);

  const setShowChat = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_CHAT', payload: show });
  }, []);

  const setShowParticipants = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_PARTICIPANTS', payload: show });
  }, []);

  const setShowSettings = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_SETTINGS', payload: show });
  }, []);

  const setShowComments = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_COMMENTS', payload: show });
  }, []);

  const setShowChannelControl = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_CHANNEL_CONTROL', payload: show });
  }, []);

  const setChatMessages = useCallback((messages: ChatMessage[]) => {
    dispatch({ type: 'SET_CHAT_MESSAGES', payload: messages });
  }, []);

  const setNewMessage = useCallback((message: string) => {
    dispatch({ type: 'SET_NEW_MESSAGE', payload: message });
  }, []);

  const setHasConnected = useCallback((hasConnected: boolean) => {
    dispatch({ type: 'SET_HAS_CONNECTED', payload: hasConnected });
  }, []);

  const setConnectionAttempts = useCallback((attempts: number) => {
    dispatch({ type: 'SET_CONNECTION_ATTEMPTS', payload: attempts });
  }, []);

  const setDeviceSelectionState = useCallback((deviceState: DeviceSelectionState) => {
    dispatch({ type: 'SET_DEVICE_SELECTION_STATE', payload: deviceState });
  }, []);

  const setConnectionQualityInfo = useCallback((qualityInfo: ConnectionQualityInfo) => {
    dispatch({ type: 'SET_CONNECTION_QUALITY_INFO', payload: qualityInfo });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  // Placeholder functions - these will be implemented by the hooks
  const connectToRoom = useCallback(async (roomId: string) => {
    // This will be implemented by the useRoomConnection hook
    console.log('connectToRoom called with:', roomId);
  }, []);

  const disconnectFromRoom = useCallback(async () => {
    // This will be implemented by the useRoomConnection hook
    console.log('disconnectFromRoom called');
  }, []);

  const toggleVideo = useCallback(async () => {
    // This will be implemented by the useMediaControls hook
    console.log('toggleVideo called');
  }, []);

  const toggleAudio = useCallback(async () => {
    // This will be implemented by the useMediaControls hook
    console.log('toggleAudio called');
  }, []);


  const startLiveStream = useCallback(async () => {
    // This will be implemented by the useLiveStreaming hook
    console.log('startLiveStream called');
  }, []);

  const stopLiveStream = useCallback(async () => {
    // This will be implemented by the useLiveStreaming hook
    console.log('stopLiveStream called');
  }, []);

  const sendMessage = useCallback(async (message: string) => {
    // This will be implemented by the useChat hook
    console.log('sendMessage called with:', message);
  }, []);

  const contextValue: LiveStudioContextType = {
    roomState: state.roomState,
    liveStreamState: state.liveStreamState,
    uiControlsState: state.uiControlsState,
    connectionState: state.connectionState,
    connectionQualityInfo: state.connectionQualityInfo,
    videoRefs,
    profile,
    deviceSelectionState: {
      cameras: deviceSelection.cameras,
      microphones: deviceSelection.microphones,
      selectedCameraId: deviceSelection.selectedCameraId,
      selectedMicrophoneId: deviceSelection.selectedMicrophoneId,
      isLoading: deviceSelection.isLoading,
      error: deviceSelection.error,
    },

    // Actions
    connectToRoom,
    disconnectFromRoom,
    toggleVideo,
    toggleAudio,
    startLiveStream,
    stopLiveStream,
    sendMessage,
    setShowChat,
    setShowParticipants,
    setShowSettings,
    setShowComments,
    setShowChannelControl,
    setShowLeaveConfirmation,
    setRoomName,
    setError,
    setStreamError,
    setParticipants,
    setConnectionQualityInfo,

    // Device selection actions
    switchCamera: deviceSelection.switchCamera,
    switchMicrophone: deviceSelection.switchMicrophone,
    refreshDevices: async () => {
      await deviceSelection.refreshDevices();
    },
  };

  return (
    <LiveStudioContext.Provider value={contextValue}>
      {children}
    </LiveStudioContext.Provider>
  );
};

// Custom hook to use the context
export const useLiveStudio = (): LiveStudioContextType => {
  const context = useContext(LiveStudioContext);
  if (context === undefined) {
    throw new Error('useLiveStudio must be used within a LiveStudioProvider');
  }
  return context;
};
